import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import ImageCompare from './ImageCompare';


const ImageArea = ({
  selectedFile,
  beforeImage,
  afterImage,
  beforeAfterMode,
  results,
  isRunning,
  onFileSelect,
  onRemoveImage,
  onDownloadImage,
  onDownloadAfterImage,
  onFullscreenToggle,
  onCropImage,
  isFullscreen
}) => {
  const [isDragOver, setIsDragOver] = useState(false);

  const { getRootProps: getMainRootProps, getInputProps: getMainInputProps, isDragActive: isMainDragActive } = useDropzone({
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        onFileSelect(acceptedFiles[0]);
      }
      setIsDragOver(false);
    },
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp']
    },
    multiple: false
  });

  const { getRootProps: getOverlayRootProps, getInputProps: getOverlayInputProps, isDragActive: isOverlayDragActive } = useDropzone({
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        onFileSelect(acceptedFiles[0]);
      }
      setIsDragOver(false);
    },
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp']
    },
    multiple: false
  });

  // Handle drag events for the entire image area
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if we're leaving the entire container
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      onFileSelect(files[0]);
    }
  };

  return (
    <div 
      className="image-area"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >

      
      {/* Drag and Drop Area - Show when no image is displayed */}
      {(!selectedFile && !beforeAfterMode) && (
        <div 
          {...getMainRootProps()} 
          className={`upload-area ${isMainDragActive ? 'drag-over' : ''}`}
          style={{ 
            display: 'flex',
            zIndex: isMainDragActive ? 1000 : 10
          }}
        >
          <input {...getMainInputProps()} />
          <div className="upload-content">
            <div className="upload-icon">📷</div>
            <p>Drag & drop an image here, or click to select</p>
            <p className="upload-hint">Supports PNG, JPG, JPEG, GIF, WEBP</p>
          </div>
        </div>
      )}

      {/* Drag and Drop Overlay - Show when dragging over the image area */}
      {isDragOver && (
        <div 
          className="upload-area overlay drag-over"
          style={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(4px)'
          }}
        >
          <div className="upload-content">
            <div className="upload-icon">📷</div>
            <p>Drop image to upload</p>
            <p className="upload-hint">Supports PNG, JPG, JPEG, GIF, WEBP</p>
          </div>
        </div>
      )}

      {/* Uploaded Image Preview */}
      {selectedFile && !beforeAfterMode && results.length === 0 && (
        <div className="uploaded-image-container">
          <img 
            src={URL.createObjectURL(selectedFile)} 
            alt="Uploaded" 
            className="uploaded-image"
          />
          <div className="image-controls">
            <button 
              onClick={onCropImage}
              className="crop-image-btn"
              title="Crop image"
            >
              ✂️
            </button>
            <button 
              onClick={onRemoveImage}
              className="remove-image-btn"
              title="Remove image"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Before/After Comparison */}
      {beforeAfterMode && beforeImage && afterImage && (
        <div className="compare-mode-container">
          <div className="compare-header">
          </div>
          <div className="compare-content">
            <ImageCompare 
              beforeImage={beforeImage}
              afterImage={afterImage}
            />
          </div>
          <div className="compare-controls">
            <button 
              onClick={onDownloadAfterImage}
              className="download-btn"
              title="Download result"
            >
              ↓ Download
            </button>

            <button 
              onClick={onFullscreenToggle}
              className={`fullscreen-btn${isFullscreen ? ' active' : ''}`}
              title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
            >
              ⛶
            </button>
          </div>
        </div>
      )}

      {/* Results Grid - Fallback when compare mode is off */}
      {results.length > 0 && !beforeAfterMode && (
        <div className="results-container">
          <div className="results-grid">
            {results.map((image, index) => (
              <div key={index} className="result-item">
                <img 
                  src={`data:image/png;base64,${image.data}`} 
                  alt={`Result ${index + 1}`}
                  className="result-image"
                />
                <div className="result-controls">
                  <button 
                    onClick={() => onDownloadImage(image)}
                    className="download-btn"
                    title="Download image"
                  >
                    ↓
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Loading State - Only show when there's content to overlay */}
      {isRunning && (selectedFile || beforeAfterMode || results.length > 0) && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Processing...</p>
        </div>
      )}
    </div>
  );
};

export default ImageArea;
